import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrayFieldValue, GroupedFieldValues } from '../../types'
import useLocationState from '../useLocationState'
import { YesObject } from '@yes.technology/react-toolkit'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'

export const SELECT_AND_LINK_PARAM = 'select_and_link'

export const SELECT_AND_LINK_POST_MESSAGE_TYPE = 'select_and_link'

export const CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE = 'cancel_select_and_link'

export const usePersistentSelection = () => {
  const navigateMergingState = useNavigateMergingState()
  const { locationState } = useLocationState()

  const getPersistentSelection = ({
    targetField,
    targetGroup,
    targetComponent,
    groupedFieldValues,
    values
  }: {
    targetField?: string
    targetGroup?: string
    targetComponent?: string
    groupedFieldValues: GroupedFieldValues
    values: YesObject[]
  }) => {
    if (targetComponent === 'ObjectView') {
      return values
    }

    if (!targetField || !targetGroup) {
      return groupedFieldValues
    }

    const groupedFields = JSON.parse(
      JSON.stringify(groupedFieldValues)
    ) as GroupedFieldValues

    if (groupedFields[targetGroup][targetField]) {
      groupedFields[targetGroup][targetField].value = values
    }

    return groupedFields
  }

  const [searchParams] = useSearchParams()

  const shouldRenderSelectAndLink = () => {
    const enabledInUrl = searchParams.has(SELECT_AND_LINK_PARAM)

    return !!(locationState?.sitemodelHistory?.length || enabledInUrl)
  }

  const location = useLocation()

  const getRedirectToObjectclassCallback = ({
    targetField,
    targetGroup,
    objectclassUuid,
    filterState,
    targetComponent,
    selectedOptions
  }: {
    targetField?: string
    targetGroup?: string
    objectclassUuid: string
    filterState?: GroupedFieldValues
    targetComponent: string
    selectedOptions?: ArrayFieldValue
  }) => {
    const { sitemodelHistory = [] } = locationState || {}
    const targetPath = `/${window.selectAndLinkFilterSitemodel}?uuid_objectclass=${objectclassUuid}`
    const currentPath = {
      url: location.pathname + location.search,
      targetField,
      targetGroup,
      targetComponent,
      filterState,
      selectedOptions
    }

    return () => {
      navigateMergingState(targetPath, {
        state: {
          sitemodelHistory: [...sitemodelHistory, currentPath],
          shouldResetData: true,
          shouldPersistData: false
        }
      })
    }
  }

  const selectAndLink = (values: YesObject[]) => {
    if (!shouldRenderSelectAndLink()) {
      return
    }

    const { sitemodelHistory = [] } = locationState
    const path = sitemodelHistory.pop()

    if (!path) {
      window.parent.postMessage(
        {
          type: SELECT_AND_LINK_POST_MESSAGE_TYPE,
          values
        },
        '*'
      )

      return
    }

    const persistentSelection = getPersistentSelection({
      targetField: path.targetField,
      targetGroup: path.targetGroup,
      targetComponent: path.targetComponent,
      groupedFieldValues: path.filterState,
      values
    })

    navigateMergingState(path.url, {
      state: {
        sitemodelHistory,
        persistentSelection,
        targetComponent: path.targetComponent,
        targetField: path.targetField,
        targetGroup: path.targetGroup,
        shouldPersistData: true,
        shouldResetData: false
      }
    })
  }

  const navigate = useNavigate()

  const cancelSelectAndLink = () => {
    const { sitemodelHistory = [] } = locationState
    const path = sitemodelHistory.pop()

    if (!path) {
      window.parent.postMessage(
        {
          type: CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE
        },
        '*'
      )

      return
    }

    navigate(-1)
  }

  return {
    selectAndLink,
    cancelSelectAndLink,
    getPersistentSelection,
    getRedirectToObjectclassCallback,
    shouldRenderSelectAndLink
  }
}
